import _cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  HERMES_ENDPOINT,
  MESSAGE_TYPE_SUBSCRIBE,
  SUBSCRIPTION_TYPE_TRIM_PROGRESS,
} from 'constants/hermes';
import { EMBED_VIDEO_PROCESSING_INFORMATION_UPDATED } from 'constants/messages';
import { LOOM_WS_URI } from 'constants/routes';
import { updateVideoCreator } from 'creators/videoPlayer';
import Websocket from 'utilities/websocket';

import './styles.less';

const LUNA_UPDATE_RETRYING =
  "Grrr Luna seems to have run off. Don't worry - we'll find her!";
const WAITING_LUNA_UPDATE =
  "Waiting on Luna to start trimming. She's wrapping up another trim right now.";

const LUNA_UPDATES = [
  "Luna is polishing your trims, give her a few minutes, she's still getting used to this!",
  "Luna is working hard to trim your video. She's getting a big treat for this one.",
  'Luna loves you in this video. Your viewers will too!',
  "Luna has your video in post-production. Trust us, it's in good paws.",
  'Luna is stitching the frames together. Not easy without opposable thumbs.',
  "Luna's working hard on your video. She's up for a promotion soon!",
  "Luna loves what you've done with the video. Two paws way up!",
  "Luna heard your voice while trimming the video. She thinks it's lovely.",
  'Your video\'s in "Paws-t" production, hang in there!',
  'Luna’s working as fast as she can. She’ll be done in two shakes of her tail.',
  'Luna is snipping at your video, please be patient!',
  'Luna is paws-itive your video will be done soon!',
  'Luna wants to be taken for walk while waiting for your video to be trimmed.',
];

class TrimProgressPanel extends Component {
  constructor(props) {
    super(props);

    this.timeLeftInterval = null;
    this.websocket = null;

    const { trim_ranges: trimRanges } = props.processingInformation;

    const trimTimeInMs = trimRanges.reduce((totalTime, trimRange) => {
      return totalTime + (trimRange.to - trimRange.from);
    }, 0);

    const newDurationInMs = props.duration * 1000 - trimTimeInMs;

    // 200ms for each segment (seek time) + 50% of the time of the new duration
    this.totalEstimatedTime = 200 * trimRanges.length + 0.5 * newDurationInMs;

    this.state = {
      current_progress: props.trimProgress,
      finalizing: false,
      luna_update: WAITING_LUNA_UPDATE,
      time_left: this.getTimeLeftRaw(props.trimProgress),
      websocket_retries: 0,
      websocket_retrying: false,
    };
  }

  render() {
    const { current_progress: currentProgress } = this.state;
    const { websocket_retrying: websocketRetrying } = this.state;
    const { time_left: timeLeft } = this.state;
    const { EditVideoButtons } = this.props;

    const style = {
      height: `${currentProgress || 0}%`,
    };

    let mobileTimeRemaining = null;

    let timeRemaining = null;

    if (timeLeft != null && !websocketRetrying) {
      const durationMoment = moment.duration(timeLeft);
      const minutes = durationMoment.minutes();
      const seconds = durationMoment.seconds();

      let timeRemainingCopy = `${seconds} sec remaining`;

      if (minutes > 0) {
        timeRemainingCopy = `${minutes} min ${timeRemainingCopy}`;
      }

      mobileTimeRemaining = (
        <p className="time-remaining mobile">{timeRemainingCopy}</p>
      );
      timeRemaining = (
        <p className="time-remaining desktop">{timeRemainingCopy}</p>
      );
    }

    let { luna_update: lunaUpdate } = this.state;

    if (websocketRetrying) {
      lunaUpdate = LUNA_UPDATE_RETRYING;
    }

    return (
      <div className="video-player-trim-progress-panel">
        <span className="progress-fill" style={style} />
        <div className="luna-flex-container">
          <div className="luna-container">
            <img
              className="luna-head"
              src={require('img/video-player/luna-head.svg')}
            />
            <img
              className="luna-body"
              src={require('img/video-player/luna-body.svg')}
            />
            <p className="luna-update">{lunaUpdate}</p>
            {mobileTimeRemaining}
          </div>
        </div>
        {timeRemaining}
        {EditVideoButtons && (
          <div className="edit-video-buttons absolute right:0 text:right">
            <EditVideoButtons startCollapsed waitingForTrim />
          </div>
        )}
      </div>
    );
  }

  async componentDidUpdate() {
    const { current_progress: currentProgress, finalizing } = this.state;

    if (finalizing || currentProgress !== 100) {
      return;
    }

    this.setState({ finalizing: true });

    // allow 100% completion to sit for a second because it feels good :-)
    await new Promise(resolve => window.setTimeout(resolve, 1000));

    const { isMainVideoOnSharePage, video } = this.props;
    const patchedVideo = _cloneDeep(video);

    patchedVideo.processing_information.trim_progress = 100;

    // update the processing information for the share video view
    if (isMainVideoOnSharePage) {
      window.top.postMessage(
        {
          type: EMBED_VIDEO_PROCESSING_INFORMATION_UPDATED,
          processing_information: patchedVideo.processing_information,
        },
        '*'
      );
    }

    this.props.updateVideoCreator(patchedVideo);
  }

  componentDidMount() {
    this.setupUpdateSocket();
  }

  componentWillUnmount() {
    if (this.websocket != null) {
      this.websocket.close();
    }

    window.clearInterval(this.timeLeftInterval);
  }

  getTimeLeftRaw(progress = 0) {
    return ((100 - progress) / 100) * this.totalEstimatedTime;
  }

  getTimeLeft(forcedProgress = null) {
    const { current_progress: currentProgress } = this.state || {};

    const progress = forcedProgress != null ? forcedProgress : currentProgress;

    if (progress === null) {
      return null;
    }

    let timeLeft = this.getTimeLeftRaw(progress);

    // never allow less than 0 seconds left
    timeLeft = Math.max(timeLeft, 0);

    // never allow the time left to jump up
    if (this.state.time_left != null) {
      timeLeft = Math.min(timeLeft, this.state.time_left);
    }

    return timeLeft;
  }

  async setupUpdateSocket() {
    const { websocket_retries: websocketRetries } = this.state;

    if (websocketRetries > 0) {
      // exponential wait: 2, 4, 8, 16
      await new Promise(resolve => {
        window.setTimeout(
          resolve,
          2000 * Math.pow(2, Math.max(websocketRetries, 4))
        );
      });
    }

    const { videoId } = this.props;

    this.websocket = new Websocket(LOOM_WS_URI + HERMES_ENDPOINT);

    this.websocket.ready(() => {
      this.setState({
        websocket_retries: 0,
        websocket_retrying: false,
      });

      this.websocket.send(
        JSON.stringify({
          type: MESSAGE_TYPE_SUBSCRIBE,
          subscription_data: {
            type: SUBSCRIPTION_TYPE_TRIM_PROGRESS,
            video_id: videoId,
          },
        })
      );
    });

    let updatedLunaUpdate = false;

    this.websocket.onMessage(({ data }) => {
      const message = JSON.parse(data);
      const { progress } = message;

      const newState = {
        current_progress: message.progress,
        time_left: this.getTimeLeft(message.progress),
      };

      if (progress != null && !updatedLunaUpdate) {
        updatedLunaUpdate = true;
        newState.luna_update =
          LUNA_UPDATES[Math.floor(Math.random() * LUNA_UPDATES.length)];

        // once a second, decrement the time left by a second
        // we use 1.5s here because otherwise it "feels" like the counter
        // decrements too fast
        this.timeLeftInterval = window.setInterval(() => {
          this.setState({
            time_left: Math.max(this.state.time_left - 1000, 0),
          });
        }, 1500);
      }

      this.setState(newState);
    });

    // if an error occurs, retry the socket connection
    this.websocket.onError(() => {
      const { websocket_retries: websocketRetries } = this.state;

      this.setState({
        websocket_retrying: true,
        websocket_retries: websocketRetries + 1,
      });

      this.setupUpdateSocket();
    });
  }
}

const mapStateToProps = state => {
  const { video, isMainVideoOnSharePage } = state.video_player;

  const { processing_information: processingInformation } = video;
  const { trim_progress: trimProgress } = processingInformation;
  const { id: videoId, video_properties: videoProperties } = video;
  const { duration } = videoProperties;

  return {
    duration,
    processingInformation,
    trimProgress,
    videoId,
    video,
    isMainVideoOnSharePage,
  };
};

const mapDispatchToProps = dispatch => {
  return { updateVideoCreator: video => dispatch(updateVideoCreator(video)) };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrimProgressPanel);
